import React, {useCallback} from 'react'
import {defineMessages, useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {useChangeMapLayout} from 'hooks/useChangeMapLayout/useChangeMapLayout'
import {getIsMapOpen, getLayout} from 'modules/search/selectors'
import {MapDisplayAction} from 'modules/search/types'

import {Button} from '@vio/atlas/Button'
import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {SkeletonLoader} from '@vio/atlas/SkeletonLoader'
import {cssTheme} from '@vio/atlas/themes'
import minus from '@vio/atlas/utils/minus'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'

const MAP_TOGGLE_BUTTON_WIDTH = 146

const MapToggleWrapper = styled.div`
  display: flex;
  background-image: ${() =>
    `url(${
      process.env.NEXT_PUBLIC_ASSETS_ENDPOINT as string
    }static/map_toggle_background.png)`};
  background-repeat: no-repeat;
  background-size: ${MAP_TOGGLE_BUTTON_WIDTH}px;
  width: ${MAP_TOGGLE_BUTTON_WIDTH}px;
  min-width: ${MAP_TOGGLE_BUTTON_WIDTH}px;
  padding: ${cssTheme.layout.spacing.s300} 0;
  align-self: center;
  justify-content: center;
  margin-top: ${minus(cssTheme.layout.spacing.s300)};
  margin-bottom: ${minus(cssTheme.layout.spacing.s300)};
`

const ButtonWrapped = styled(Button)`
  display: inline-block;
  * :first-letter {
    text-transform: uppercase;
  }
`

const SkeletonLoaderWrapper = styled(SkeletonLoader)`
  align-self: center;
`

const mapToggleButtonMessage = defineMessages({
  hide: {
    id: 'map.hide',
    defaultMessage: 'hide map'
  },
  show: {
    id: 'map.show',
    defaultMessage: 'Show map'
  }
})

export const MapToggle = () => {
  const dispatch = useDispatch()
  const {formatMessage} = useIntl()
  const layout = useSelector(getLayout)
  const {isDesktopMd, isDesktopLg} = useDeviceLayout()
  const mapHasSplitView = isDesktopMd || isDesktopLg
  const isMapOpen = useSelector(getIsMapOpen)
  const buttonMessageLabel = isMapOpen ? 'hide' : 'show'
  const changeMapLayout = useChangeMapLayout()

  const toggleMapLayout = useCallback(() => {
    if (isMapOpen) {
      changeMapLayout(MapDisplayAction.HIDE, mapHasSplitView)
    } else {
      changeMapLayout(MapDisplayAction.SHOW, mapHasSplitView)
    }

    dispatch(
      trackEvent({
        category: Category.User,
        entity: Entity.MapViewToggle,
        action: Action.Clicked
      })
    )
  }, [dispatch, isMapOpen, mapHasSplitView, changeMapLayout])

  // added as layout can return undefined until syncStoreWithUrl has finished
  if (!layout) {
    return (
      <SkeletonLoaderWrapper width={MAP_TOGGLE_BUTTON_WIDTH} marginTop={0} />
    )
  }

  return (
    <MapToggleWrapper>
      <ButtonWrapped
        dataId="MapToggleButton"
        variant="tertiary"
        isFloating
        size="md"
        onClick={() => toggleMapLayout()}
      >
        {formatMessage(mapToggleButtonMessage[buttonMessageLabel])}
      </ButtonWrapped>
    </MapToggleWrapper>
  )
}
